import {
  BadgeCheck,
  Banknote,
  CircleDot,
  ClipboardCopy,
  FileText,
  MessageSquareText,
  RotateCcw,
  ShieldCheck,
  Undo2,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import useTranslations from "@/shared/lang/useTranslations";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { PaymentIncidentAction, PaymentIncidentEvent } from "../types/paymentIncidents.types";

const ACTION_ICON: Record<PaymentIncidentAction, LucideIcon> = {
  opened: CircleDot,
  report_copied: ClipboardCopy,
  provider_responded: MessageSquareText,
  provider_refunded: Banknote,
  note: MessageSquareText,
  resolved: BadgeCheck,
  dismissed: XCircle,
  reopened: RotateCcw,
  client_refunded: Undo2,
};

export interface IncidentTimelineProps {
  events: PaymentIncidentEvent[];
  provider: string;
}

/**
 * Everything that happened on the incident, oldest first: who did it, when it happened (and when it was
 * written down, if that was another day), the note, the screenshots and whether it took the second factor.
 */
export function IncidentTimeline({ events, provider }: IncidentTimelineProps) {
  const { t, activeLanguage } = useTranslations("paymentIncidents");
  if (!events.length) return null;

  return (
    <ol className="relative space-y-4 border-l border-border pl-5" aria-label={t("timeline_label")}>
      {events.map((event) => {
        const Icon = ACTION_ICON[event.action] ?? CircleDot;
        const occurred = formatDateTime(event.occurred_at, { locale: activeLanguage, includeTime: true });
        const recordedLater =
          new Date(event.created_at).toDateString() !== new Date(event.occurred_at).toDateString();
        const followUp = event.action === "report_copied" && event.metadata.follow_up;
        return (
          <li key={event.id} className="relative">
            <span className="absolute -left-[29px] top-0.5 flex size-[18px] items-center justify-center rounded-full border border-border bg-background">
              <Icon className="size-2.5 text-muted-foreground" aria-hidden="true" />
            </span>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="text-sm font-medium text-foreground">
                {t(followUp ? "event_report_copied_follow_up" : `event_${event.action}`, { provider })}
              </p>
              {event.two_factor_verified && (
                <span
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"
                  title={t("event_two_factor_hint")}
                >
                  <ShieldCheck className="size-3" aria-hidden="true" />
                  {t("event_two_factor")}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {event.actor_email} · {occurred}
              {recordedLater &&
                ` · ${t("event_recorded_on", {
                  date: formatDateTime(event.created_at, { locale: activeLanguage }),
                })}`}
            </p>
            {event.metadata.provider_case_reference && (
              <p className="mt-1 text-xs text-foreground">
                {t("event_case_reference", { reference: event.metadata.provider_case_reference })}
              </p>
            )}
            {event.action === "resolved" && event.metadata.resolution && (
              <p className="mt-1 text-xs text-foreground">{t(`resolution_${event.metadata.resolution}`)}</p>
            )}
            {event.action === "client_refunded" && event.metadata.amount && event.metadata.currency && (
              <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-foreground">
                <ColocaAmountDisplay
                  value={event.metadata.amount}
                  currency={event.metadata.currency}
                  fractionDigits={minorUnitsFor(event.metadata.currency)}
                  locale={activeLanguage}
                  size="sm"
                />
                {event.metadata.top_up_reference && (
                  <span className="font-mono text-muted-foreground">{event.metadata.top_up_reference}</span>
                )}
              </p>
            )}
            {event.note && event.action !== "opened" && (
              <p className="mt-1.5 whitespace-pre-wrap break-words rounded-md bg-muted/40 px-3 py-2 text-sm text-foreground">
                {event.note}
              </p>
            )}
            {event.note && event.action === "opened" && (
              <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-foreground">{event.note}</p>
            )}
            {event.attachments.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2" aria-label={t("event_attachments")}>
                {event.attachments.map((file, index) => (
                  <li key={`${event.id}-${index}`}>
                    <IncidentAttachmentThumb file={file} />
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function IncidentAttachmentThumb({ file }: { file: PaymentIncidentEvent["attachments"][number] }) {
  const { t } = useTranslations("paymentIncidents");
  const isImage = file.content_type.startsWith("image/");
  const label = t("event_attachment_open", { name: file.filename });
  if (!file.url) {
    return (
      <span className="flex h-16 w-24 items-center justify-center rounded-md border border-dashed border-border px-2 text-center text-[11px] text-muted-foreground">
        {t("event_attachment_unavailable")}
      </span>
    );
  }
  return (
    <a
      href={file.url}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={label}
      title={file.filename}
      className="group block overflow-hidden rounded-md border border-border focus-visible:outline-2 focus-visible:outline-ring"
    >
      {isImage ? (
        <img
          src={file.url}
          alt={file.filename}
          loading="lazy"
          className="h-16 w-24 object-cover transition-opacity group-hover:opacity-90"
        />
      ) : (
        <span className="flex h-16 w-24 flex-col items-center justify-center gap-1 px-2 text-[11px] text-muted-foreground">
          <FileText className="size-4" aria-hidden="true" />
          <span className="w-full truncate text-center">{file.filename}</span>
        </span>
      )}
    </a>
  );
}
